import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { CacheItem, TransferHttpService } from 'src/app/services/transfer-http/transfer-http.service';
import { AllSites, InvInventoryItemReceived, InvAttachedToTypes, InvInventoryItem, WoWorkOrders, AllLocations, AllSubLocations } from 'src/models';
import { AimsHub, ServerPageParams } from 'src/app/components/aimsHub/aimsHub';
import { map, take } from 'rxjs/operators';
import { AimsCommonConfirmDialogComponent, AimsCommonPopupEditorComponent, AimsCommonTableButtonColor, AimsCommonTableFilterType, AimsCommonTableHeader, AimsCommonTableHeaderType, AimsCommonTablePermissions, AimsCommonTableSelection, SessionStorageService, TableRowUpdate, TableState } from '@aims/common-components';
import { FormBuilder, Validators } from '@angular/forms';
import { InventoryAdjustmentPopupComponent } from './inventory-adjustment-popup/inventory-adjustment-popup.component';
import { MatDialog } from '@angular/material/dialog';
import { GetTextPipe } from 'src/app/pipes';
import { Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { AddInventoryToCartPopupComponent } from './add-inventory-to-cart-popup/add-inventory-to-cart-popup.component';
import { InventoryCartPopupComponent } from './inventory-cart-popup/inventory-cart-popup.component';
import { isPlatformBrowser } from '@angular/common';
import { MessageService } from 'src/app/services/message/message.service';
import { DialogComponent } from 'src/app/services/message/message.enum';
import { PermissionsService } from 'src/app/services/permissions/permissions.service';
import { AIMS_NUMERIC_TYPES } from 'src/app/components/aims-numeric/aims-numeric.component';
import { GridAlert } from 'src/app/add-ins/grid-alert';
import { AimsGridSelectorComponent } from 'src/app/components/aims-grid-selector/aims-grid-selector.component';
import moment from 'moment-mini';

@Component({
  selector: 'app-view-inventory',
  templateUrl: './view-inventory.component.html',
  styleUrls: ['./view-inventory.component.css'],
  providers: [
    { provide: 'inventory', useClass: AimsHub },
    { provide: 'inventoryRec', useClass: AimsHub },
    { provide: 'inventoryAdj', useClass: AimsHub },
    { provide: 'inventoryExp', useClass: AimsHub },
    GetTextPipe
  ]
})
export class ViewInventoryComponent implements OnInit {
  constructor(private fb: FormBuilder, private transferHttp: TransferHttpService,
            @Inject('inventory') private inventoryHub: AimsHub,
            @Inject('inventoryRec') private inventoryRecHub: AimsHub,
            @Inject('inventoryAdj') private inventoryAdjHub: AimsHub,
            @Inject('inventoryExp') private inventoryExpHub: AimsHub,
            private router: Router,
            private messageService: MessageService,
            private sessionStorage: SessionStorageService,
            public permissions: PermissionsService,
            @Inject(PLATFORM_ID) private platformId: Object,
            public getTextPipe: GetTextPipe, private dialog: MatDialog) {
  }

  public readonly HUB_ID: string = '/inventoryList';
  // helper for grid alerts
  public gridAlert: GridAlert;

  //Spinner
  spinnerText: string = "Loading...";
  showSpinner: boolean = false;

  AIMS_NUMERIC_TYPES = AIMS_NUMERIC_TYPES;

  defaultTableState: TableState = { sort:{ active : 'barcode', direction: 'asc' }};

  // AimsCommonTable setup
  invTablePermissions: AimsCommonTablePermissions = {
    create: false,
    update: true,
    delete: false,
    export: true,
    print: false
  };
  header: AimsCommonTableHeader[] = [
    { key: 'select', label: '', type: 'checkbox', filter: false, tooltip: 'Select Rows' },
    { key: 'rowExpand', label: '', type: 'rowExpand', filter: false },
    { key: 'details', label: 'Details', type: 'button', prefix: '/inventory/inventoryItem/', linkId: 'inventoryItemId', filter: false },
    { key: 'adjust', label: 'Adjust', type: 'button',
        button: { color: AimsCommonTableButtonColor.PRIMARY, icon: 'build' },
        hidden: !this.permissions.canUpdate(this.permissions.ComponentID.InventoryAdjust),
        events: { click: (element) => this.openAdjustmentPopup(element) }, filter: false },
    { key: 'expend', label: 'Cart', type: 'button',
        button: { color: AimsCommonTableButtonColor.PRIMARY, icon: 'add_shopping_cart' },
        hidden: !this.permissions.canUpdate(this.permissions.ComponentID.InventoryExpend),
        events: { click: (element) => this.openAddToCartPopup(element) }, filter: false },
    { key: 'inventoryItemId', label: '', type: AimsCommonTableHeaderType.KEY, hidden: true},
    { key: 'barcode', label: 'Barcode', type: 'string', editable: false, width: 160, tooltip: 'Barcode or Id' },
    { key: 'description', label: 'Description', type: 'string', width: 300, editable: false, tooltip: 'Item Description' },
    { key: 'partNo', label: 'Part #', type: 'string', width: 100, editable: false },
    { key: 'siteId', label: 'Site', type: 'dynamic', editable: false,
      filterType: AimsCommonTableFilterType.DYNAMIC, events: { filterAndSortCompare: (term, el) => term === el.siteId } },
    { key: 'workSectionId', label: 'Work Section', type: 'dynamic', editable: false,
      filterType: AimsCommonTableFilterType.DYNAMIC },
    { key: 'inventoryGroup.inventoryGroupCode', label: 'Group', type: 'string', width: 60 },
    { key: 'quantity', label: 'Qty', type: 'dynamic', width: 70, editable: false,
         events: { filterAndSortCompare: (term, el) => this.getInventoryQuantity(el.inventoryItemId).toString().includes(term),
                    filterAndSort: (el) =>  this.getInventoryQuantity(el.inventoryItemId)
         }
     },
    { key: 'minQty', label: 'Min Qty', type: 'number', width: 50, editable: false, tooltip: 'Minimum On Hand' },
    { key: 'authorizedQty', label: 'Auth Qty', type: 'number', width: 50, editable: false  },
    { key: 'daysRemaining', label: 'Days Inv Remaining', type: 'dynamic', width: 70, editable: false,
         events: { filterAndSortCompare: (term, el) => this.getDaysInvRemaining(el.inventoryItemId).toString().includes(term),
                    filterAndSort: (el) =>  this.getDaysInvRemaining(el.inventoryItemId)
         },
         tooltip: 'Days of remaining inventory based of consumption for last 30 days.'
     },
    { key: 'locationId', label: 'Location', type: 'dynamic', filterType: AimsCommonTableFilterType.DYNAMIC,
      events: { filterAndSortCompare: (term, el) => term === el.locationId } },
    { key: 'subLocationId', label: 'Sub-Location', type: 'dynamic', filterType: AimsCommonTableFilterType.DYNAMIC,
      events: { filterAndSortCompare: (term, el) => term === el.subLocationId } },
  ];

 public headerFull: AimsCommonTableHeader[] =
  [
    ...this.header,

    { key: 'unitOfIssueId', label: 'Unit of Issue', type: 'string', width: 60, editable: false  },
    { key: 'hazmat', label: 'Hazmat', type: AimsCommonTableHeaderType.BOOLEAN, filterType: AimsCommonTableFilterType.BOOLEAN, width: 60, tooltip: 'Check if HazMat' },
    { key: 'workOrderRequired', label: 'WO Req', type: AimsCommonTableHeaderType.BOOLEAN, filterType: AimsCommonTableFilterType.BOOLEAN, width: 60 },
    { key: 'assemblyIssued', label: 'Assembly Issued', type: AimsCommonTableHeaderType.BOOLEAN, filterType: AimsCommonTableFilterType.BOOLEAN },
    { key: 'difm', label: 'DIFM', type: AimsCommonTableHeaderType.BOOLEAN, filterType: AimsCommonTableFilterType.BOOLEAN },
    { key: 'nsn', label: 'NSN', type: AimsCommonTableHeaderType.STRING, },
    { key: 'autoReorder', label: 'Auto-Reorder', type: AimsCommonTableHeaderType.BOOLEAN, filterType: AimsCommonTableFilterType.BOOLEAN },
    { key: 'ownerId', label: 'Owner', type: 'dynamic',  filterType: 'dynamic', editable: false,
      events: { filterAndSortCompare: (term, el) => term === el.ownerId } },
    { key: 'errc', label: 'ERRC', type: AimsCommonTableHeaderType.STRING, },
    { key: 'isActive', label: 'IsActive', type: AimsCommonTableHeaderType.BOOLEAN, filterType: AimsCommonTableFilterType.BOOLEAN}
  ];

  elementReceivedHeader: AimsCommonTableHeader[] = [
    { key: 'editButton', label: 'Edit', type: 'editButton', },
    { key: 'receivedDate', label: 'Date', type: 'date', validatorOrOpts: [Validators.required]},
    { key: 'originalQuantity', label: 'Received', type: 'number', validatorOrOpts: [Validators.required, Validators.min(1)]},
    { key: 'quantity', label: 'Remaining', type: 'number', validatorOrOpts: [Validators.required, Validators.min(0)]},
    { key: 'costPerUnit', label: 'Unit Cost', type: 'dynamic', validatorOrOpts: [Validators.required]},
  ];
  elementAdjustmentsHeader: AimsCommonTableHeader[] = [
    { key: 'adjustmentDate', label: 'Date', type: 'date', },
    { key: 'quantity', label: 'Quantity', type: 'number', },
    { key: 'adjustedByUserId', label: 'Adjusted by', type: 'dynamic', },
    { key: 'note', label: 'Note', type: 'string', },
  ];
  elementExpendedHeader: AimsCommonTableHeader[] = [
    { key: 'pulledDate', label: 'Date', type: 'date', },
    { key: 'quantity', label: 'Quantity', type: 'number', },
    { key: 'pulledByEmployeeId', label: 'User', type: 'dynamic', },
    { key: 'attachedToType', label: 'Attached To', type: 'dynamic', },
    { key: 'note', label: 'Note', type: 'string', },
  ];

  private storageName = 'cartInventory';

  public inventory = this.inventoryHub.getHot(this.HUB_ID);
  // #2710: the following is used for initial server side paging, but needs to be rethought
  // public inventory = this.inventoryHub.getHot(this.HUB_ID, null, this.getPaginationParams());

  public expandedInvId: BehaviorSubject<number> = new BehaviorSubject(null);
  //filter down to id of opened inventory item
  private invRec = new BehaviorSubject([]);
  public inventoryRec = combineLatest([this.expandedInvId, this.invRec]).pipe(map(([id,recVals]) => {
    return recVals.filter(i => i.inventoryItemId == id);
  }));
  private invAdj = new BehaviorSubject([]);
  public inventoryAdj = combineLatest([this.expandedInvId, this.invAdj]).pipe(map(([id,adjVals]) => {
    return adjVals.filter(i => i.inventoryItemId == id);
  }));
  private invExp = new BehaviorSubject([]);
  public inventoryExp = combineLatest([this.expandedInvId, this.invExp]).pipe(map(([id,expVals]) => {
    return expVals.filter(i => i.inventoryItemId == id);
  }));

  public employeeValues = this.transferHttp.getCache('employeeDropdown');
  public owners = this.transferHttp.getSingle('Facilities/GetOwners');
  public attachedToTypes: Observable<Array<InvAttachedToTypes>> = this.transferHttp.getSingle('Inventory/GetAllowedAttachedToTypes');
  public sites = this.transferHttp.getCache<Array<AllSites>>(CacheItem.sites);
  public inventoryAttachedToType: InvAttachedToTypes[] = [];
  public wos = this.transferHttp.getSingle(`Facilities/GetSiteWorkOrders?siteId=null&basicGrid=true`);
  public workSections = this.transferHttp.getSingle('Hr/GetWorkSections');
  public locations = this.transferHttp.getSingle<Array<AllLocations>>('Facilities/GetLocations');
  public subLocations = this.transferHttp.getSingle<Array<AllSubLocations>>('Facilities/GetSubLocations');
  tenantConfig = this.transferHttp.tenantConfig;
  inventoryTenantConfig = this.tenantConfig.inventoryConfig;
  ownersList: any;
  selectedItems: any[] = [];
  workSectionList: any;
  locationList: any;
  subLocationList: any;
  activeInventory: boolean;
  inventoryList: Observable<any[]>;

  // grid selection
  grid: string;
  basicGrid: boolean;

  inventoryListChecked() {
    this.activeInventory = !this.activeInventory;
    this.inventoryList = this.activeInventory ?
        this.inventory.pipe(map(items => items.filter(item => item.isActive == this.activeInventory))) :
        this.inventory;
  }

  public handleRowExpand(element: any) {
      this.expandedInvId.next(element?.inventoryItemId);
  }

  public getInventoryQuantity(invId: number, inventoryRec: Array<any> = null): number {
    let invRec = inventoryRec || this.invRec.value;
    let arrTest = invRec.filter(r => r.inventoryItemId == invId && r.quantity != null).map(r => r.quantity);
    let retVal = arrTest.length > 0 ? arrTest.reduce((sum,x) => sum + x||0) : 0;
    return retVal;
  }

  public getDaysInvRemaining(invId: number, inventoryRec: Array<any> = null, inventoryExp: Array<any> = null): number {
    let invRec = inventoryRec || this.invRec.value;
    let qty: number = this.getInventoryQuantity(invId, invRec);

    let invExp = inventoryExp || this.invExp.value;
    let expIn30Days: number = invExp.filter(e => e.inventoryItemId == invId && e.quantity != null && moment(e.pulledDate).isAfter(moment().subtract(30,'days'))).map(e => e.quantity).reduce((sum,x) => sum + x, 0);

    let daysRemaining: number = qty / (expIn30Days/30);

    if (isNaN(daysRemaining) || !isFinite(daysRemaining)) {
      daysRemaining = 0;
    }

    return daysRemaining;

  }

  // testing for grid alert.
  // public sendLatestAlert() {
  //   this.transferHttp.get('Alerts/SendLatestAlerts').subscribe(ret => {
  //     var x = ret;
  //   });
  // }
  // public sendDailyAlerts() {
  //   this.transferHttp.get('Alerts/SendDailyAlerts').subscribe(ret => {
  //     var x = ret;
  //    });
  // }

    gridChange(grid: string) {
   	  this.grid = grid;
      this.basicGrid = (grid == AimsGridSelectorComponent.BASIC);
    }

  // TODO: make this a common utility function
  private getPaginationParams() {
      const tableSessionState = this.sessionStorage.get(`inventory-table`);
      const tableState = typeof tableSessionState == 'string' ? JSON.parse(tableSessionState) : tableSessionState;

      const pageParams: ServerPageParams = tableState ? tableState : this.defaultTableState;
      if (!pageParams.paginator) {
        pageParams.paginator = {
          pageIndex: 0,
          pageSize: 10,
        }
      }
      pageParams.pageCount = 3;
      console.log("pageParams: ", pageParams)

      return pageParams;
  }

  getForExcel = (data: any) => {
    var exportData = data.map(eir => {
      var x = {
        'inventoryItemId': eir.inventoryItemId,
        'barcode': eir.barcode,
        'description': eir.description,
        'partNo': eir.partNo,
        'siteId': this.getTextPipe.transform(eir.siteId, 'siteId', 'siteCode', this.sites),
        'workSectionId': this.getTextPipe.transform(eir.workSectionId, 'workSectionId', 'workSection', this.workSectionList),
        'inventoryGroup.inventoryGroupCode': eir.inventoryGroup?.inventoryGroupCode,
        'quantity': this.getInventoryQuantity(eir.inventoryItemId),
        'minQty': eir.minQty,
        'authorizedQty': eir.authorizedQty,
        'locationId': this.getTextPipe.transform(eir.locationId, 'locationId', 'location', this.locationList),
        'subLocationId': this.getTextPipe.transform(eir.subLocationId, 'subLocationId', 'subLocation', this.subLocationList),
      }

      if (this.grid === 'full') {
        let y = {
          ...x,
        'unitOfIssueId': eir.unitOfIssueId,
        'hazmat': eir.hazmat,
        'workOrderRequired': eir.workOrderRequired,
        'assemblyIssued': eir.assemblyIssued,
        'difm': eir.difm,
        'nsn': eir.nsn,
        'autoReorder': eir.autoReorder,
        'ownerId': this.getTextPipe.transform(eir.ownerId, 'equipmentOwnerId','equipmentOwner', this.ownersList),
        'isActive': eir.isActive,
        'errc': eir.errc
        }
        return y;
      }

      return x;
    });
    return { 'data': exportData  };
  }

  ngOnInit(): void {
    window.scrollTo(0,0);

    this.gridAlert = new GridAlert(this.HUB_ID, this.transferHttp);
    this.inventoryRecHub.getHot('inventoryRecList').subscribe(ret => this.invRec.next(ret));
    this.inventoryAdjHub.getHot('inventoryAdjList').subscribe(ret => this.invAdj.next(ret));
    this.inventoryExpHub.getHot('inventoryExpList').subscribe(ret => this.invExp.next(ret));

    this.attachedToTypes.subscribe(ret => {
      this.inventoryAttachedToType = ret;
    });

    this.owners.subscribe(o => {
        this.ownersList = o;
    });

    this.locations.subscribe(l => { this.locationList = l; });
    this.subLocations.subscribe(l => { this.subLocationList = l; });
    this.wos.subscribe(wos => this.workOrders = wos);
    this.inventory.subscribe(i => console.log("inv: ", i));

    this.workSections.subscribe(o => {
        this.workSectionList = o;
    });

    this.restoreCart();

    this.activeInventory = true
    this.inventoryList = this.inventory
      .pipe(map(items => items.filter(item => item.isActive == this.activeInventory)));


  }

  workOrderNumber: string = null;
  openTicketPrint() {
    if (this.workOrderNumber){
        this.transferHttp.getSingle(`Inventory/GetWorkOrderIdList?workOrderNumber=${this.workOrderNumber}`).subscribe((woIds:Array<number>) => {
            let url = '/reporting/Inv_IssueTicket/trdp/';
            if (woIds.length == 1){
                url += JSON.stringify({ workOrderId: woIds[0], workOrderIdList:woIds })
            }
            else if (woIds.length > 1) {
                url += JSON.stringify({workOrderIdList:woIds});
            }
           window.open(url,'_blank');
        });
    }
    else{
        window.open(`/reporting/Inv_IssueTicket/trdp`,'_blank');
    }

  }

  handleReceiveRowUpdate(event: TableRowUpdate) {
      this.spinnerText = "Saving Changes...";
      this.showSpinner = true;
      const itemRec: InvInventoryItemReceived = event.newRow as InvInventoryItemReceived;
      this.transferHttp.put('Inventory/UpdateInventoryReceived',itemRec).pipe(take(1)).subscribe(_ => {
          this.showSpinner = false;
      })
  }

  restoreCart() {
    if (isPlatformBrowser(this.platformId)) {
      const s = window.sessionStorage.getItem(this.storageName);
      if (s !== null) {
        let obj = JSON.parse(s);
        this.cart = obj as Cart;
      }
    }
  }

  storeCart() {
    if (isPlatformBrowser(this.platformId)) {
      window.sessionStorage.setItem(this.storageName, JSON.stringify(this.cart));
    }
  }

  clearCart() {
    if (isPlatformBrowser(this.platformId)) {
      window.sessionStorage.removeItem(this.storageName);
    }
  }

  public handleSelections(selection: AimsCommonTableSelection) {
    this.selectedItems = selection.selections;
  }
  public handleAllSelections(selection: AimsCommonTableSelection) {
    this.selectedItems = selection.selections;
  }

  public handlePrint(format: string) {

    if (this.selectedItems.length == 0)
    {
       this.inventory
          .subscribe(inventoryItems => {
            let barcodes = inventoryItems.map(ii => ii.barcode);
            this.printMultipleBarcodes(barcodes, format);
          });
    }
    else {
        let barcodes: Array<string> = this.selectedItems.map(ii => ii.barcode);
        this.printMultipleBarcodes(barcodes, format);
    }
  }

  public printMultipleBarcodes (barcodes: string[], format: string) {
    let key: string = 'Barcodes'
    sessionStorage.setItem(key, JSON.stringify({ 'Barcodes': barcodes}));
    this.router.navigate([`/reporting/InventoryBarcodeMultiple${format}/trdp/{}/${key}`]);
  }

  public adjustInventoryItemQuantity(inventoryItemIdToUpdate: number, updatedInventoryItemQuantity: number, note: string) : void {

    this.spinnerText = "Updating Inventory Item...";
    this.showSpinner = true;

    var url = `inventory/AdjustInventoryItemQuantity?inventoryItemIdToUpdate=${inventoryItemIdToUpdate}&updatedInventoryItemQuantity=${updatedInventoryItemQuantity}&note=${note}`;
    this.transferHttp.put(url, null)
      .subscribe(retVal => {
        this.showSpinner = false;
    });
  }

  public openAdjustmentPopup(itemToAdjust) {
    const originalQuantity = this.getInventoryQuantity(itemToAdjust.inventoryItemId);

    const dialogRef = this.dialog.open(AimsCommonPopupEditorComponent, {
            data: {
                header: 'Adjust Item Quantity',
                content: InventoryAdjustmentPopupComponent,
                contentData: { itemToAdjust: itemToAdjust, quantity: originalQuantity },
            },
            disableClose: true
        });

        dialogRef.afterClosed().subscribe(dialogResult => {
          if (dialogResult && dialogResult.adjustedQuantity != originalQuantity) {
            this.adjustInventoryItemQuantity(itemToAdjust.inventoryItemId, dialogResult.adjustedQuantity, dialogResult.adjustmentNote);
          }
        });
    }

  public cart: Cart = new Cart();

  public addInventoryToCart(inventoryItem, addedInventoryItemQuantity: number, currentQuantity: number ) : void {
    // check if new item site is same as items already in cart
    if (this.cart.cartItems.length > 0) {
      let cartItem: CartItem = this.cart.cartItems[0];
      if (cartItem.inventoryItem.siteId != inventoryItem.siteId) {
        this.showErrorDialog("All Cart items must be from same Site");
        return;
      }
    }

    // check if item already in cart
    const cartItem: CartItem = this.cart.cartItems.find(i => i.inventoryItem.inventoryItemId == inventoryItem.inventoryItemId);
    if (cartItem ) {
      if (cartItem.quantity + addedInventoryItemQuantity > currentQuantity) {
        // item quantity exceeds current quantity
        return;
      }

      cartItem.quantity+= addedInventoryItemQuantity;
    }
    else {
      this.cart.cartItems.push(new CartItem( inventoryItem, addedInventoryItemQuantity, this.getInventoryQuantity(inventoryItem.inventoryItemId)));
    }

    this.storeCart();
  }

  public totalCartItems(): number {
    let total: number = 0;
    this.cart.cartItems.forEach(ci => {
      total += ci.quantity;
    })
    return total;
  }

  public openAddToCartPopup(itemToAdd) {
    const currentQuantity = this.getInventoryQuantity(itemToAdd.inventoryItemId);

    const dialogRef = this.dialog.open(AimsCommonPopupEditorComponent, {
            data: {
                btnOkText: 'Add',
                header: 'Add to Cart',
                content: AddInventoryToCartPopupComponent,
                contentData: { itemToAdd: itemToAdd, currentQuantity: currentQuantity,
                               },
            },
            disableClose: true
        });

        dialogRef.afterClosed().subscribe(dialogResult => {
          if (dialogResult && dialogResult.addedQuantity > 0) {
            this.addInventoryToCart(itemToAdd, dialogResult.addedQuantity, currentQuantity);
          }
        });
  }

  updateAndShowCart() {
    this.updateCart(this.showShoppingCart);
  }

  workOrders: WoWorkOrders[] = [];
  public showShoppingCart() {
    const dialogRef = this.dialog.open(AimsCommonPopupEditorComponent, {
            width: '680px',
            data: {
                useRaw: true,
                btnOkText: 'Checkout',
                btnCancelText: 'Close',
                header: 'Inventory Cart',
                content: InventoryCartPopupComponent,
                contentData: { cart: this.cart, inventoryAttachedToTypes: this.inventoryAttachedToType, transferHttp: this.transferHttp, workOrders: this.workOrders },
            },
            disableClose: true
        });

    dialogRef.afterClosed().subscribe(dialogResult => {
        this.storeCart();
        if (dialogResult) {
          this.cart = dialogResult.cart;
          this.updateCart(this.checkInstock, dialogResult);
        }
      });
  }

  processCheckout(dialogResult: any) {
    let pulledForId: number = dialogResult.pulledForId;
    let attachedToType = dialogResult.attachedToType;

    if (attachedToType == 1) {
      // wo
      let woAction = dialogResult.woAction;
      if (woAction == 'lookup') {
        let workOrderId = dialogResult.workOrderId;

        this.attachCartItemsToWorkOrder(this.cart, workOrderId, pulledForId);
      }
      else {
        // create wo
        let workOrderNumber = dialogResult.workOrderNumber;
        this.createWorkOrderAndAttachCartItems(this.cart, workOrderNumber, pulledForId);
      }
    }
    else if (attachedToType == 2) {
      // asset
      let equipmentId = dialogResult.equipmentId;
      this.attachCartItemsToAsset(this.cart, equipmentId, pulledForId);
    }
    else if (attachedToType == 3) {
      // schedule item
      let scheduleItemId = dialogResult.scheduleItemId;
      this.attachCartItemsToScheduleItem(this.cart, scheduleItemId, pulledForId);
    }
    else if (attachedToType == 4) {
      // traveler
      let travelerId = dialogResult.travelerId;
      this.attachCartItemsToTraveler(this.cart, travelerId, pulledForId);
    }
    else if (attachedToType == 5) {
      // manual expenditure
      let note = dialogResult.manualNote;
      this.attachCartItemsToManualExpenditure(this.cart, note, pulledForId);
    }
  }

  showErrorDialog(message: string) {
    var dialogData = this.messageService.getDialogMessage(DialogComponent.AimsError, message);
            let dialogRef = this.dialog.open(AimsCommonConfirmDialogComponent, {
              data: dialogData,
              disableClose: true
    });
    dialogRef.afterClosed().subscribe((dialogResult) => { });
  }

  updateCart(fn: Function, data: any = null) {
    let ids: number[] = [];
    this.cart.cartItems.forEach(ci => {
      ids.push(ci.inventoryItem.inventoryItemId);
    });

    this.transferHttp.put('inventory/GetInventoryItemsById', ids).subscribe(items => {
      this.cart.cartItems.forEach(ci => {
        let stockUpdate = items.find(s => s.inventoryItemId == ci.inventoryItem.inventoryItemId);
        ci.instock = stockUpdate.quantity;
      });
      data ? fn.bind(this)(data) : fn();
    });
  }

  checkInstock(dialogResult: any) {
    const cart = dialogResult.cart;
    if (cart.cartItems.some(ci => ci.instock < ci.quantity)) {
      this.showShoppingCart();
    }
    else {
       this.processCheckout(dialogResult);
    }
  }

  attachCartItemsToWorkOrder(cart: Cart, workOrderId: number, pulledForId: number): void {
    this.spinnerText = "Attaching Inventory to Work Order...";
    this.showSpinner = true;

    // convert CartItems to ...
    let itemsToBeAttached = cart.cartItems.map(c => {
        return { inventoryItemId: c.inventoryItem.inventoryItemId, quantity: c.quantity } });
    var url = `inventory/AttachInventoryToWorkOrder?workOrderId=${workOrderId}&pulledForId=${pulledForId}`;
    this.transferHttp.put(url, itemsToBeAttached)
      .subscribe({
        next: resp => {
          this.showSpinner = false;
          this.clearAndDeleteCart();

          //Open Issue Ticket report
          const reportUrl = `/reporting/Inv_IssueTicket/trdp/${JSON.stringify({ticketNumber:resp.julian,workOrderId:resp.workOrderId})}`;
          window.open(reportUrl,"_blank");
        },
        error: err => {
          this.showSpinner = false;
        }
      });
  }

  attachCartItemsToAsset(cart: Cart, equipmentId: number, pulledForId: number): void {
    this.spinnerText = "Attaching Inventory to Asset...";
    this.showSpinner = true;

    // convert CartItems to ...
    let itemsToBeAttached = cart.cartItems.map(c => {
        return { inventoryItemId: c.inventoryItem.inventoryItemId, quantity: c.quantity } });
    var url = `inventory/AttachInventoryToAsset?equipmentId=${equipmentId}&pulledForId=${pulledForId}`;
    this.transferHttp.put(url, itemsToBeAttached)
      .subscribe(_ => {
        this.showSpinner = false;
        this.clearAndDeleteCart();
    });
  }

  createWorkOrderAndAttachCartItems(cart: Cart, workOrderNumber: number, pulledForId: number) {
    this.spinnerText = "Creating new Work Order...";
    this.showSpinner = true;

    let siteId = cart.cartItems[0].inventoryItem.siteId;
    // create WO
    this.transferHttp.post(`Facilities/CreateWorkOrder?workOrderNumber=${workOrderNumber}&siteId=${siteId}`, null).subscribe({
      next: retval => {
        const workOrderId = retval.id;
        this.attachCartItemsToWorkOrder(cart, workOrderId, pulledForId);
      },
      error: err => {
        this.showErrorDialog(`Could not create Work Order: ${err.message}`);
        this.showSpinner = false;
      }
    });
  }

  attachCartItemsToManualExpenditure(cart: Cart, note: string, pulledForId: number) {
    this.spinnerText = "Attaching Inventory to Manual Expenditure...";
    this.showSpinner = true;

    // convert CartItems to ...
    let itemsToBeAttached = cart.cartItems.map(c => {
        return { inventoryItemId: c.inventoryItem.inventoryItemId, quantity: c.quantity } });
    var url = `inventory/AttachInventoryToManualExpenditure?note=${note}&pulledForId=${pulledForId}`;
    this.transferHttp.put(url, itemsToBeAttached)
      .subscribe(retVal => {
        this.showSpinner = false;
        this.clearAndDeleteCart();
    });
  }

  attachCartItemsToScheduleItem(cart: Cart, scheduleItemId: number, pulledForId: number) {
    this.spinnerText = "Attaching Inventory to Schedule Item...";
    this.showSpinner = true;

    // convert CartItems to ...
    let itemsToBeAttached = cart.cartItems.map(c => {
        return { inventoryItemId: c.inventoryItem.inventoryItemId, quantity: c.quantity } });
    var url = `inventory/AttachInventoryToScheduleItem?scheduleItemId=${scheduleItemId}&pulledForId=${pulledForId}`;
    this.transferHttp.put(url, itemsToBeAttached)
      .subscribe(retVal => {
        this.showSpinner = false;
        this.clearAndDeleteCart();
    });
  }

   attachCartItemsToTraveler(cart: Cart, travelerId: number, pulledForId: number) {
    this.spinnerText = "Attaching Inventory to Traveler...";
    this.showSpinner = true;

    // convert CartItems to ...
    let itemsToBeAttached = cart.cartItems.map(c => {
        return { inventoryItemId: c.inventoryItem.inventoryItemId, quantity: c.quantity } });
    var url = `inventory/AttachInventoryToTraveler?travelerId=${travelerId}&pulledForId=${pulledForId}`;
    this.transferHttp.put(url, itemsToBeAttached)
      .subscribe(retVal => {
        this.showSpinner = false;
        this.clearAndDeleteCart();
    });
  }

  clearAndDeleteCart() {
    this.cart = new Cart();
    this.clearCart();
  }

}


// classes
export class Cart {
  public cartItems: Array<CartItem> = [];
}

export class CartItem {
  inventoryItem: InvInventoryItem;
  quantity: number;
  instock: number;

  public constructor(item: InvInventoryItem, quantity: number, instock: number) {
    this.inventoryItem = item;
    this.quantity = quantity;
    this.instock = instock;
  }
}

export class ItemToBeAttached {
  inventoryItemId: number;
  quantity: number;
}
